import { Menu, X, Star, MapPin } from 'lucide-react';
import { useState, useEffect } from 'react';

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      
      // Calculate scroll progress
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = docHeight > 0 ? Math.min(100, (scrollTop / docHeight) * 100) : 0;
      setScrollProgress(scrollPercent);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  return (
    <header className={`sticky top-0 z-50 transition-all duration-300 ${
      isScrolled 
        ? 'bg-navy-900/95 backdrop-blur-md shadow-luxury-xl border-b border-gold-400/20' 
        : 'bg-navy-900/95 shadow-luxury border-b border-gold-400/20'
    }`}>
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo and Brand */}
          <div className="flex items-center">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center overflow-hidden">
                <img src="/Logo.png" alt="Hotel A.S.K Grand Logo" className="w-full h-full object-contain" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gold-400">Hotel A.S.K Grand</h1>
                <div className="hidden sm:flex items-center gap-2 text-xs text-cream-300">
                  <MapPin className="w-3 h-3 text-gold-400" />
                  <span>Luxury in Gajuwaka, Visakhapatnam</span>
                </div>
              </div>
            </div>
            
            {/* Rating Badge - Desktop */}
            <div className="hidden lg:flex items-center ml-8 glass-luxury px-4 py-2 rounded-full border border-gold-400/30">
              <div className="flex items-center gap-1">
                {/* 4 full stars */}
                {[...Array(4)].map((_, i) => (
                  <Star key={i} className="w-3 h-3 text-gold-400 fill-current" />
                ))}
                {/* 70% filled star for 4.8 rating */}
                <div className="relative">
                  <Star className="w-3 h-3 text-cream-400 fill-current" />
                  <Star className="w-3 h-3 text-gold-400 fill-current absolute top-0 left-0 overflow-hidden" style={{ clipPath: 'inset(0 30% 0 0)' }} />
                </div>
              </div>
              <span className="ml-2 text-sm font-medium text-cream-200">4.8 (500+ luxury reviews)</span>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-2">
            {[
              { id: 'rooms', label: 'Luxury Suites' },
              { id: 'amenities', label: 'Amenities' },
              { id: 'location', label: 'Location' },
              { id: 'contact', label: 'Contact' }
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="px-5 py-3 text-white hover:text-gold-400 hover:bg-gold-400/10 rounded-xl transition-all duration-200 font-medium border border-transparent hover:border-gold-400/30"
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-4">
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden mobile-menu-toggle p-3 text-white hover:text-gold-400 rounded-lg transition-colors"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <div className={`md:hidden transition-all duration-300 ease-in-out ${
          isMenuOpen 
            ? 'max-h-96 opacity-100 pb-6' 
            : 'max-h-0 opacity-0 overflow-hidden'
        }`}>
          <div className="glass-luxury rounded-3xl p-6 space-y-3 border border-gold-400/20">
            {/* Rating Badge - Mobile */}
            <div className="flex items-center justify-center mb-6 glass-luxury px-4 py-3 rounded-full border border-gold-400/30">
              <div className="flex items-center gap-1">
                {/* 4 full stars */}
                {[...Array(4)].map((_, i) => (
                  <Star key={i} className="w-3 h-3 text-gold-400 fill-current" />
                ))}
                {/* 70% filled star for 4.8 rating */}
                <div className="relative">
                  <Star className="w-3 h-3 text-cream-400 fill-current" />
                  <Star className="w-3 h-3 text-gold-400 fill-current absolute top-0 left-0 overflow-hidden" style={{ clipPath: 'inset(0 30% 0 0)' }} />
                </div>
              </div>
              <span className="ml-2 text-sm font-medium text-cream-200">4.8 Luxury Rating</span>
            </div>

            {/* Navigation Links */}
            {[
              { id: 'rooms', label: 'Our Luxury Suites' },
              { id: 'amenities', label: 'Premium Amenities' },
              { id: 'location', label: 'Prime Location' },
              { id: 'contact', label: 'Contact Concierge' }
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="block w-full text-left px-5 py-4 text-white hover:text-gold-400 hover:bg-gold-400/10 rounded-2xl transition-all duration-200 font-medium border border-transparent hover:border-gold-400/30"
              >
                {item.label}
              </button>
            ))}

            {/* Mobile CTAs */}
            <div className="flex gap-3 pt-6 border-t border-gold-400/20">
            </div>
          </div>
        </div>
      </div>

      {/* Luxury Progress Bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-navy-800">
        <div 
          className="h-full transition-all duration-300 shadow-gold"
          style={{ 
            width: `${scrollProgress}%`,
            background: 'linear-gradient(135deg, #957F3E 0%, #b8965a 25%, #d4b376 50%, #957F3E 100%)'
          }}
        />
      </div>
    </header>
  );
}
